/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import "../../../components/Drawers/DeviceDrawer.css";
import { useSelector } from "react-redux";
import { IDusNodeDataGridRow, IFullDeviceDetails, INrCell, ISectorProvision } from "../../../store/api/deviceConfig/types";
import { setOpenDuDrawer } from "../../../store/api/devices/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { isOpenDuDrawerSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useDuNodeDrawer } from "./hooks";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import { IDuCellInfo } from "../../../store/api/devices/types";
import AddEditCellInformation from "./AddEditCellInformation";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR, PROVISION_CHECK_VAL } from "../../../globals/globals";
import Provisioning from "./Provisioning";
import SectorProvisioninig from "./SectorProvisioning";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import { Sheet } from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Loader2 } from "lucide-react";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";

interface IDeviceDrawerProps {
  data?: IDusNodeDataGridRow;
  closedButtonClicked: (arg: boolean) => void;
  rowsData?: Array<IDusNodeDataGridRow>;
  cellList?: INrCell | null;
  isCellDrawer?: string;
}

const DUDrawer: React.FC<IDeviceDrawerProps> = ({ data, closedButtonClicked, rowsData = [], cellList, isCellDrawer = "" }) => {
  const dispatch = useStoreDispatch();
  const {
    permissions,
    radioNodeFormData,
    setRadioNodeFormData,
    setSectorHubs,
    selectedRadio,
    setSelectedRadio,
    zones,
    setZones,
    zoneOption,
    cellFormDataOne,
    setCellFormDataOne,
    cellFormDataTwo,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    isCreateCellRadioInfoTwo,
    setIsCreateCellRadioInfoTwo,
    handleSubmitRadioNode,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    isAddEditRadioTwo,
    setIsAddEditRadioTwo,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    errMsg2,
    isLoadingConfigStatus,
    radios,
    duBandData,
    setDuBandData,
    onBlurProvision,
    updateProvisioning,
    provisionIndex,
    setProvisionIndex,
    errProvision,
    setRadioNodeList,
    nrCellRowsData,
    sectorFormData,
    setSectorFormData,
    updateSectorProvisioning,
    isLoadingCellInfo,
    cellStatus,
  } = useDuNodeDrawer(data?.model || "", data?.id || 0);

  const isOpenDuDrawer = useSelector(isOpenDuDrawerSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);

  React.useEffect(() => {
    if (data) {
      setRadioNodeFormData(data);
      if (data?.radioUnit && Array.isArray(data?.radioUnit?.sectorDevices) && data?.radioUnit?.sectorDevices?.length > 0) {
        let sectorCount: any = [];
        sectorCount = data?.radioUnit?.sectorDevices?.filter(
          (el) => el?.ethernetAddress?.toLowerCase() === data?.radioUnit?.ethernetId?.toLowerCase()
        );
        setSectorHubs([...sectorCount]);
      }
    }
  }, [data]);

  React.useEffect(() => {
    if (rowsData?.length > 0) {
      setRadioNodeList(rowsData);
    }
  }, [rowsData, setRadioNodeList]);

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }
    dispatch(setOpenDuDrawer(open));
    closedButtonClicked(open);
  };

  const handleChange = (name: string, value: string) => {
    setRadioNodeFormData({ ...radioNodeFormData, [name]: value });
  };

  const handleSetSelectedZones = (newValue: any | null, type: string) => {
    setZones(newValue);
  };

  const handleTabChange = (newTab: string) => {
    setSelectedRadio(newTab);
  };
  const handleSelectBand = (val: string, type: string) => {
    if (type === "radio1") setDuBandData(val);
    else return;
  };

  const handleUpdateCellInfoOption = (newState: boolean, type: string) => {
    if (type === "radio1") setIsCreateCellRadioInfoOne(!newState);
    else setIsCreateCellRadioInfoTwo(!newState);
  };

  const handleUpdatedCellInfo = (formData: IDuCellInfo, type: string) => {
    if (type === "radio1") setCellFormDataOne(formData);
    else setCellFormDataTwo(formData);
  };

  const submitRadioNodeData = (type: string) => {
    if (type === "delete") {
      handleDeleteRadioCell();
    } else handleSubmitRadioNode(type);
  };

  const updateProvisionRadioVal = (val: number) => {
    setProvisionIndex(val);
  };

  const handleUpdateSectorInfo = (formData: ISectorProvision) => {
    setSectorFormData(formData);
  };

  /*  
  const checkEditType = (cellId: any) => {
    if(cellId) setIsAddEditRadioOne("edit")
    else setIsAddEditRadioOne("add")
  }
*/

  const handleAddRadioCellInfoOne = (cellId: any) => {
    setIsCreateCellRadioInfoOne(!isCreateCellRadioInfoOne);
    if (cellId) setIsAddEditRadioOne("edit");
    else setIsAddEditRadioOne("add");
  };

  const handleClose = () => {
    dispatch(setOpenDuDrawer(false));
    closedButtonClicked(false);
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      {radioNodeFormData && radioNodeFormData?.id >= PROVISION_CHECK_VAL && (
        <Button
          disabled={selectedDevice?.status === 19}
          onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.DUPROVISION))}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Provisioned
        </Button>
      )}
      {permissions?.hasOpCodeGroup("manage_topology") && radioNodeFormData?.id < PROVISION_CHECK_VAL && (
        <Button
          disabled={isLoadingConfigStatus || selectedDevice?.status === 19}
          onClick={() => handleSubmitRadioNode("edit")}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Save
        </Button>
      )}
    </>
  );

  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.DUPROVISION && (
        <Provisioning
          setProvision={updateProvisionRadioVal}
          provisionVal={provisionIndex}
          onBlurHandler={onBlurProvision}
          onSubmitData={updateProvisioning}
          errMsg={errProvision}
          isLoadingConfigStatus={isLoadingConfigStatus}
        />
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.ADDSECTORHUB && (
        <SectorProvisioninig
          formData={sectorFormData}
          setFormData={handleUpdateSectorInfo}
          onBlurHandler={onBlurProvision}
          onSubmitData={updateSectorProvisioning}
          errMsg={errProvision}
          isLoadingConfigStatus={isLoadingConfigStatus}
        />
      )}
      <Sheet open={isOpenDuDrawer} onClose={handleClose} title={data?.name || "Unknown"} footer={footer} width="560px">
        <div className="space-y-5">
          <StatusIcon status={data?.status || ""} />

          <div className="flex items-center gap-2">
            <Checkbox id="du-enable" disabled checked={radioNodeFormData?.enable || false} />
            <Label htmlFor="du-enable">Enable</Label>
          </div>

          <InputField
            name="name"
            label="Name"
            disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL}
            value={radioNodeFormData?.name || ""}
            onChange={handleChange}
          />

          <InputField
            name="macAddress"
            label="MAC Address"
            disabled
            value={radioNodeFormData?.ethernetId || ""}
            onChange={handleChange}
          />

          <InputField
            name="description"
            label="Description"
            disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL}
            value={radioNodeFormData?.description || ""}
            onChange={handleChange}
          />

          {/* Provision SectorHub button */}
          {permissions?.hasOpCodeGroup("manage_topology") && radioNodeFormData?.id < PROVISION_CHECK_VAL && data?.radioUnit === null && (
            <div className="flex justify-end">
              <Button
                disabled={isLoadingConfigStatus}
                onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDSECTORHUB))}
              >
                Provision SectorHub
              </Button>
            </div>
          )}

          {/* Sector hub info */}
          {radioNodeFormData?.id < PROVISION_CHECK_VAL && data?.radioUnit !== null && (
            <>
              <h3 className="text-base font-semibold text-foreground">Sector hub</h3>
              <StatusIcon status={data?.status || ""} />

              <InputField
                name="name"
                label="Name"
                disabled
                value={data?.radioUnit?.name || ""}
                onChange={handleChange}
              />

              <InputField
                name="ethernetAddress"
                label="MAC Address"
                disabled
                value={data?.radioUnit?.ethernetId || ""}
                onChange={handleChange}
              />
            </>
          )}

          {/* Radio Tabs */}
          {!(radioNodeFormData?.id >= PROVISION_CHECK_VAL) && !!radioNodeFormData?.radioUnit?.index && (
            <Tabs value={selectedRadio} onValueChange={handleTabChange}>
              <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                <TabsTrigger
                  value="radio1"
                  className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Radio 1
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="h-px bg-border" />

          {/* Loading Cell Info */}
          {isLoadingCellInfo && (
            <div className="flex items-center gap-2">
              <Loader2 size={20} className="animate-spin text-primary" />
              <span className="text-base font-semibold text-foreground">Getting Cell Info...</span>
            </div>
          )}

          {/* Cell Information */}
          {!(radioNodeFormData?.id >= PROVISION_CHECK_VAL) && !isLoadingCellInfo && !!radioNodeFormData?.radioUnit?.index ? (
            selectedRadio === "radio1" ? (
              isCreateCellRadioInfoOne ? (
                <AddEditCellInformation
                  selectedRadioNode={radioNodeFormData}
                  cellFormData={cellFormDataOne}
                  setCellFormData={handleUpdatedCellInfo}
                  isCreateRadioCell={isCreateCellRadioInfoOne}
                  cellType={selectedRadio}
                  status={cellStatus || ""}
                  handleUpdateCellInfoOption={handleUpdateCellInfoOption}
                  zones={zones}
                  zoneOption={zoneOption}
                  selectedBand={"nr-band-77"}
                  handleSelectBand={handleSelectBand}
                  handleSetSelectedZones={handleSetSelectedZones}
                  isAddEditType={isAddEditRadioOne}
                  handleSubmit={submitRadioNodeData}
                  onBlurHandler={onBlurHandler}
                  errMsg={errMsg}
                  configStatus={isLoadingConfigStatus}
                  radios={radios}
                  bandData={duBandData}
                  nrCellListData={nrCellRowsData}
                />
              ) : (
                permissions?.hasOpCodeGroup("manage_topology") &&
                !(radioNodeFormData?.id >= PROVISION_CHECK_VAL) &&
                !isCreateCellRadioInfoOne && (
                  <>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-foreground">Cell Information</h3>
                      <Button onClick={() => handleAddRadioCellInfoOne(cellFormDataOne?.cellId)}>
                        ADD CELL
                      </Button>
                    </div>
                    <p className="text-sm" style={{ color: "var(--warning)" }}>
                      No cells have been added. Click "ADD CELL" to add.
                    </p>
                  </>
                )
              )
            ) : null
          ) : null}
        </div>
      </Sheet>
    </>
  );
};

export default DUDrawer;
